import { createSlice } from "@reduxjs/toolkit";
import { getPatientRecordThunk, getConsultentList, getPatientNotes } from "../thunk";
import { logout } from "../slices";

const initialState = {
  byId: {},
};

const patientRecordSlice = createSlice({
  name: "patientRecord",
  initialState,
  reducers: {
    clearPatientRecord: (state, action) => {
      if (action.payload) {
        delete state.byId[action.payload];
      } else {
        state.byId = {};
      }
    },
    setPatientFromNav: (state, action) => {
      const { id, patient } = action.payload;
      if (!state.byId[id]) state.byId[id] = {};
      state.byId[id].patient = patient;
    },
  },
  extraReducers: (builder) => {
    builder
      // `patientLoading` exists for the deep-linked "/patientrecord/:id" form of the page,
      // which arrives with no nav state and has to fetch the patient itself. Without a
      // pending flag it cannot distinguish "still fetching" from "fetched nothing", so it
      // would flash an empty record before the data lands — and would re-dispatch on every
      // render while the first request was still in flight.
      .addCase(getPatientRecordThunk.pending, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].patientLoading = true;
        state.byId[id].patientError = null;
      })
      .addCase(getPatientRecordThunk.fulfilled, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].patientLoading = false;
        if (action.payload?.success) {
          state.byId[id].patient = action.payload.data;
          state.byId[id].patientError = null;
        } else {
          state.byId[id].patientError = action.payload?.message;
        }
      })
      .addCase(getPatientRecordThunk.rejected, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].patientLoading = false;
        state.byId[id].patientError = action.payload?.message || "Failed to fetch";
      })
      .addCase(getConsultentList.pending, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].vitalsLoading = true;
        state.byId[id].vitalsError = null;
      })
      .addCase(getConsultentList.fulfilled, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].vitalsLoading = false;
        if (action.payload?.success) {
          state.byId[id].vitals = action.payload.data;
          state.byId[id].vitalsError = null;
        } else {
          state.byId[id].vitalsError = action.payload?.message;
        }
      })
      .addCase(getConsultentList.rejected, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].vitalsLoading = false;
        state.byId[id].vitalsError = action.payload?.message || "Failed to fetch vitals";
      })
      .addCase(getPatientNotes.pending, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].notesLoading = true;
        state.byId[id].notesError = null;
      })
      .addCase(getPatientNotes.fulfilled, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].notesLoading = false;
        if (action.payload?.success) {
          state.byId[id].notes = action.payload.data;
          state.byId[id].notesError = null;
        } else {
          state.byId[id].notesError = action.payload?.message;
        }
      })
      .addCase(getPatientNotes.rejected, (state, action) => {
        const id = action.meta.arg;
        if (!state.byId[id]) state.byId[id] = {};
        state.byId[id].notesLoading = false;
        state.byId[id].notesError = action.payload?.message || "Failed to fetch notes";
      })
      .addCase(logout, () => initialState);
  },
});

export const { clearPatientRecord, setPatientFromNav } = patientRecordSlice.actions;
export default patientRecordSlice.reducer;
